import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { isEditableTarget } from './shortcuts';

/**
 * Full screen for the TV page (Cole, 2026-09-26: "when I cast it to TV I
 * can't get the top bar to hide"). A browser only enters full screen from a
 * click or key press, so the page offers three ways in: the button (shown
 * while the mouse moves, and for the first seconds after load), the F key,
 * and a double-click anywhere. Esc or F again leaves.
 */

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitFullscreenEnabled?: boolean;
  webkitExitFullscreen?: () => Promise<void> | void;
};
type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

const SHOW_MS = 3500;
const FIRST_SHOW_MS = 8000;

function supported(): boolean {
  const doc = document as FullscreenDocument;
  return Boolean(doc.fullscreenEnabled || doc.webkitFullscreenEnabled);
}

function isFullscreen(): boolean {
  const doc = document as FullscreenDocument;
  return Boolean(doc.fullscreenElement || doc.webkitFullscreenElement);
}

async function toggleFullscreen(): Promise<void> {
  const doc = document as FullscreenDocument;
  try {
    if (isFullscreen()) {
      if (doc.exitFullscreen) await doc.exitFullscreen();
      else await doc.webkitExitFullscreen?.();
      return;
    }
    const root = document.documentElement as FullscreenElement;
    if (root.requestFullscreen) await root.requestFullscreen({ navigationUI: 'hide' });
    else await root.webkitRequestFullscreen?.();
  } catch {
    // Refused (no user gesture, or the browser forbids it): nothing to undo.
  }
}

/** Shown while the mouse moves or a key is pressed, and for the first seconds
 *  after load. A control with keyboard focus always shows (styles.css,
 *  .top-controls:focus-within), so keyboard users never tab onto a hidden
 *  button (peer review F07). */
export function useShownOnMove(): boolean {
  const [shownUntil, setShownUntil] = useState(() => Date.now() + FIRST_SHOW_MS);
  const [, setTick] = useState(0);
  useEffect(() => {
    const onMove = () => setShownUntil(Date.now() + SHOW_MS);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('keydown', onMove);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('keydown', onMove);
    };
  }, []);
  // Re-render once the show window lapses so the controls fade out.
  useEffect(() => {
    const wait = shownUntil - Date.now();
    if (wait <= 0) return undefined;
    const timer = window.setTimeout(() => setTick((n) => n + 1), wait + 50);
    return () => window.clearTimeout(timer);
  }, [shownUntil]);
  return Date.now() < shownUntil;
}

/** `children`: more controls in the same top bar (Side view). */
export default function FullscreenControl({ children }: { children?: ReactNode }) {
  const [available] = useState(supported);
  const [full, setFull] = useState(isFullscreen);
  const visible = useShownOnMove();

  useEffect(() => {
    const onChange = () => setFull(isFullscreen());
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  useEffect(() => {
    if (!available) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.repeat || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isEditableTarget(event.target)) return;
      if (event.key === 'f' || event.key === 'F') void toggleFullscreen();
    };
    const onDoubleClick = (event: MouseEvent) => {
      if (isEditableTarget(event.target)) return;
      void toggleFullscreen();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('dblclick', onDoubleClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('dblclick', onDoubleClick);
    };
  }, [available]);

  const toggle = useCallback(() => void toggleFullscreen(), []);
  // In full screen the cursor hides with the button: a still mouse is an
  // arrow parked on the broadcast (Cole, 2026-09-26). Moving it brings both
  // back.
  const hideCursor = full && !visible;
  useEffect(() => {
    document.documentElement.classList.toggle('cursor-hidden', hideCursor);
    return () => document.documentElement.classList.remove('cursor-hidden');
  }, [hideCursor]);
  if (!available && !children) return null;
  return (
    <div className={`top-controls${visible ? ' is-visible' : ''}`}>
      {available ? (
        <button
          type="button"
          className="fullscreen-control"
          onClick={toggle}
          aria-label={full ? 'Exit full screen' : 'Full screen'}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            {full ? (
              <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />
            ) : (
              <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
            )}
          </svg>
          <span>{full ? 'Exit full screen' : 'Full screen'}</span>
          <kbd>F</kbd>
        </button>
      ) : null}
      {children}
    </div>
  );
}
